import { useEffect, useMemo, useRef, useState } from "react";
import "./quiz-guard.css";

/*
 * Copy/screenshot deterrence around a running quiz and its answer review.
 * A web page can't truly block screenshots, so this:
 *   - stops selecting, copying, cutting, dragging, right-click and printing
 *   - blanks the questions the moment the Windows/Cmd key or Print Screen goes down
 *     (before Snipping Tool / Cmd+Shift+4 grab the screen) and whenever the
 *     tab or window loses focus (another app, screen-recorder controls, tab switch)
 *   - tiles the learner's name + email across the quiz, so any photo that does
 *     get out shows who took it
 */
const BLOCKED_KEYS = new Set(["c", "x", "a", "p", "s", "u"]);

export default function QuizGuard({ active, mark, children }) {
  const [hidden, setHidden] = useState(false);
  const keyHide = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    const hideFor = (ms) => {
      setHidden(true);
      clearTimeout(keyHide.current);
      keyHide.current = setTimeout(() => setHidden(document.hidden || !document.hasFocus()), ms);
    };
    const wipeClipboard = () => navigator.clipboard?.writeText?.("").catch(() => {});

    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      if (key === "printscreen") { e.preventDefault(); hideFor(1500); wipeClipboard(); return; }
      if (key === "meta" || key === "os" || (e.metaKey && e.shiftKey)) { hideFor(1500); return; }
      if ((e.ctrlKey || e.metaKey) && BLOCKED_KEYS.has(key)) e.preventDefault();
      if (key === "f12" || ((e.ctrlKey || e.metaKey) && e.shiftKey && ["i", "j", "c"].includes(key))) e.preventDefault();
    };
    const onKeyUp = (e) => {
      if ((e.key || "").toLowerCase() === "printscreen") { hideFor(1500); wipeClipboard(); }
    };
    const away = () => { clearTimeout(keyHide.current); setHidden(true); };
    const back = () => { if (!document.hidden && document.hasFocus()) hideFor(300); };
    const onVisibility = () => (document.hidden ? away() : back());
    const block = (e) => e.preventDefault();

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("blur", away);
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeprint", away);
    document.addEventListener("copy", block, true);
    document.addEventListener("cut", block, true);
    return () => {
      clearTimeout(keyHide.current);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", away);
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeprint", away);
      document.removeEventListener("copy", block, true);
      document.removeEventListener("cut", block, true);
      setHidden(false);
    };
  }, [active]);

  const watermark = useMemo(() => {
    const text = String(mark || "").replace(/[<>&"]/g, "");
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180"><text x="160" y="95" text-anchor="middle" transform="rotate(-24 160 90)" font-family="Roboto, sans-serif" font-size="13" fill="rgba(11,15,25,0.09)">${text}</text></svg>`;
    return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
  }, [mark]);

  if (!active) return children;

  const block = (e) => e.preventDefault();
  return (
    <div className={`quiz-guard${hidden ? " is-hidden" : ""}`} onContextMenu={block} onDragStart={block} onCopy={block} onCut={block}>
      <div className="quiz-guard-body" aria-hidden={hidden}>{children}</div>
      {mark && <div className="quiz-guard-mark" style={{ backgroundImage: watermark }} aria-hidden="true" />}
      {hidden && (
        <div className="quiz-guard-cover" role="status">
          <strong>Quiz hidden</strong>
          <small>Come back to this window to continue. Your timer is still running.</small>
        </div>
      )}
    </div>
  );
}
